<template>
  <b-modal
    v-model="isOpen"
    size="lg"
    :title="'Log history: ' + (header ? header.label : '')"
    content-class="log-history-modal"
    body-class="log-history-modal-body"
    @show="onShow"
    @hidden="onHidden"
    hide-footer
  >
    <div v-if="header" class="log-history-modal-content">
      <!-- Add entry: relative on top, no overflow so datepicker can open fully -->
      <div class="log-history-add-section">
      <b-card v-if="canEdit" class="mb-3 overflow-none">
        <h6 class="mb-2">Add entry</h6>
        <b-row>
          <b-col md="4">
            <label class="small">Date</label>
            <b-form-datepicker
              v-model="newEntryDate"
              class="mb-2 log-history-datepicker"
              reset-button
              boundary="viewport"
            />
            <label class="small mt-1">Time</label>
            <b-form-timepicker
              v-model="newEntryTimestampTime"
              size="sm"
              locale="en"
              class="mb-2"
              reset-button
              boundary="viewport"
            />
          </b-col>
          <b-col md="4">
            <label class="small">Value</label>
            <input
              v-if="logValueType === 'text'"
              v-model="newEntryValue"
              class="form-control form-control-sm mb-2"
              placeholder="Value"
            />
            <template v-else-if="logValueType === 'number'">
              <input
                v-if="!hasRange"
                v-model="newEntryValue"
                class="form-control form-control-sm mb-2"
                type="number"
                :min="header.min"
                :max="header.max"
                placeholder="Value"
              />
              <div v-else class="mb-2">
                <b-form-input
                  v-model="newEntryValue"
                  size="sm"
                  type="range"
                  :min="header.min"
                  :max="header.max"
                  :step="header.step"
                />
                <p class="mb-0 small"><strong>{{ newEntryValue }}</strong><span v-if="header.suffix"> {{ header.suffix }}</span></p>
              </div>
              <span v-if="header.suffix && !hasRange" class="small text-muted">{{ header.suffix }}</span>
            </template>
            <b-form-select
              v-else-if="logValueType === 'boolean'"
              v-model="newEntryValue"
              size="sm"
              class="mb-2"
              :options="booleanOptions"
            />
            <b-form-select
              v-else-if="logValueType === 'select'"
              v-model="newEntryValue"
              size="sm"
              class="mb-2"
              :options="selectOptions"
            />
            <template v-else-if="logValueType === 'datetime'">
              <b-form-datepicker
                v-model="newEntryValue"
                size="sm"
                class="mb-2"
                reset-button
                boundary="viewport"
                placeholder="Date"
              />
              <b-form-timepicker
                v-if="header.includeTime"
                v-model="newEntryTime"
                size="sm"
                locale="en"
                class="mb-2"
                reset-button
                boundary="viewport"
              />
            </template>
            <div v-else-if="logValueType === 'image'" class="mb-2">
              <p class="small text-muted mb-1">Add one or more photos for this log entry.</p>
              <ImageUpload
                :key="'log-history-img-' + (header ? header.machine_name : '') + '-' + (newEntryImages && newEntryImages.length ? 'set' : 'empty')"
                :mediaObject="newEntryImages"
                @mediaDelivery="onNewEntryImageDelivered"
              />
            </div>
            <input
              v-else
              v-model="newEntryValue"
              class="form-control form-control-sm mb-2"
              placeholder="Value"
            />
          </b-col>
          <b-col md="4">
            <label class="small">Notes</label>
            <input
              v-model="newEntryNotes"
              class="form-control form-control-sm mb-2"
              placeholder="Optional"
            />
          </b-col>
        </b-row>
        <b-button size="sm" variant="primary" @click="addEntry" :disabled="saving || !newEntryValueValid">
          Add entry
        </b-button>
      </b-card>
      </div>

      <!-- Trend chart for numeric / boolean headers, plotted over time. -->
      <div v-if="isChartable" class="log-history-chart-section mb-3">
        <h6 class="mb-2">Trend</h6>
        <div v-show="chartPoints.length >= 2" class="log-history-chart-wrap">
          <canvas ref="logChart"></canvas>
        </div>
        <p v-if="chartPoints.length < 2" class="text-muted small mb-0">
          Add at least two readings to see a trend over time.
        </p>
      </div>

      <!-- History: fixed height, scrolls; when editing a row, overflow visible so datepicker isn't clipped -->
      <h6 class="mb-2">History</h6>
      <div class="log-history-history-section" :class="{ 'log-history-editing-active': !!editingId }">
        <div v-if="loading" class="text-muted small">Loading…</div>
        <b-list-group v-else-if="entries.length === 0" class="mb-2">
          <b-list-group-item class="text-muted">No log entries yet.</b-list-group-item>
        </b-list-group>
        <template v-else>
          <b-list-group class="mb-2">
            <b-list-group-item
              v-for="entry in entries"
              :key="entry.id"
              class="d-flex align-items-center flex-wrap"
            >
              <template v-if="editingId === entry.id && canEdit">
                <b-row class="w-100">
                  <b-col md="3" class="mb-2">
                    <label class="small d-block">Date</label>
                    <b-form-datepicker v-model="editDate" size="sm" reset-button class="log-history-edit-datepicker" boundary="viewport" />
                    <label class="small d-block mt-1">Time</label>
                    <b-form-timepicker v-model="editTimestampTime" size="sm" locale="en" reset-button boundary="viewport" class="log-history-edit-timepicker" />
                </b-col>
                <b-col md="3" class="mb-2">
                  <input
                    v-if="logValueType === 'text'"
                    v-model="editValue"
                    class="form-control form-control-sm"
                  />
                  <template v-else-if="logValueType === 'number'">
                    <input
                      v-if="!hasRange"
                      v-model="editValue"
                      class="form-control form-control-sm"
                      type="number"
                      :min="header.min"
                      :max="header.max"
                    />
                    <div v-else>
                      <b-form-input
                        v-model="editValue"
                        size="sm"
                        type="range"
                        :min="header.min"
                        :max="header.max"
                        :step="header.step"
                      />
                      <span class="small"><strong>{{ editValue }}</strong><span v-if="header.suffix"> {{ header.suffix }}</span></span>
                    </div>
                  </template>
                  <b-form-select
                    v-else-if="logValueType === 'boolean'"
                    v-model="editValue"
                    size="sm"
                    class="form-control form-control-sm"
                    :options="booleanOptions"
                  />
                  <b-form-select
                    v-else-if="logValueType === 'select'"
                    v-model="editValue"
                    size="sm"
                    class="form-control form-control-sm"
                    :options="selectOptions"
                  />
                  <template v-else-if="logValueType === 'datetime'">
                    <b-form-datepicker v-model="editValue" size="sm" reset-button boundary="viewport" class="form-control form-control-sm log-history-edit-datetime-date" />
                    <b-form-timepicker v-if="header.includeTime" v-model="editTime" size="sm" locale="en" reset-button class="form-control form-control-sm mt-1" />
                  </template>
                  <div v-else-if="logValueType === 'image'" class="mb-2">
                    <ImageUpload
                      :key="'log-history-edit-img-' + (header ? header.machine_name : '') + '-' + (editingId || 'none')"
                      :mediaObject="editImages"
                      @mediaDelivery="onEditImagesDelivered"
                    />
                    <small class="text-muted d-block mt-1">
                      Reorder, remove, or update captions for images in this entry.
                    </small>
                  </div>
                  <input
                    v-else
                    v-model="editValue"
                    class="form-control form-control-sm"
                  />
                </b-col>
                <b-col md="3" class="mb-2">
                  <input v-model="editNotes" class="form-control form-control-sm" placeholder="Notes" />
                </b-col>
                <b-col md="3">
                  <b-button size="sm" variant="success" class="mr-1" @click="saveEdit(entry)" :disabled="saving || (logValueType === 'boolean' && editValue !== true && editValue !== false)">Save</b-button>
                  <b-button size="sm" variant="secondary" @click="editingId = null">Cancel</b-button>
                </b-col>
              </b-row>
            </template>
              <template v-else>
              <span class="mr-2"><strong>{{ formatEntryDate(entry) }}</strong></span>
              <template v-if="logValueType === 'image' && entry.value && typeof entry.value === 'object'">
                <span class="mr-2 align-middle">
                  <img
                    v-if="thumbUrls[entry.id] && thumbUrls[entry.id].length"
                    :src="thumbUrls[entry.id][0]"
                    class="log-history-thumb rounded border"
                    alt="Log image"
                    @click="openImageViewer(entry)"
                  />
                  <span v-else class="small text-muted">Loading…</span>
                </span>
                <span class="mr-2 small text-muted" v-if="getImageCount(entry) > 0">
                  {{ getImageCount(entry) }} {{ getImageCount(entry) === 1 ? 'image' : 'images' }}
                </span>
                <b-button size="sm" variant="outline-secondary" class="mr-2" @click="openImageViewer(entry)">
                  View full screen
                </b-button>
              </template>
              <span v-else class="mr-2">{{ formatEntryValue(entry) }}</span>
              <span v-if="entry.notes" class="text-muted small mr-2">{{ entry.notes }}</span>
              <span v-if="canEdit" class="ml-auto">
                <b-button size="sm" variant="outline-primary" class="mr-1" @click="startEdit(entry)">Edit</b-button>
                <b-button size="sm" variant="outline-danger" @click="confirmDelete(entry)">Delete</b-button>
              </span>
            </template>
          </b-list-group-item>
        </b-list-group>
        <b-button v-if="lastDoc && entries.length >= 10" size="sm" variant="outline-secondary" class="mb-2" @click="loadMore" :disabled="loadingMore">
          Load more
        </b-button>
      </template>
      </div>
    </div>

    <template v-slot:modal-footer>
      <b-button variant="secondary" @click="close">Close</b-button>
    </template>

    <Confirm
      v-if="confirmDeleteEntry"
      text="Delete this log entry?"
      v-on:confirmed="doDelete($event)"
    />

    <!-- Full-screen image viewer -->
    <b-modal
      v-model="showImageViewer"
      size="xl"
      body-class="p-0"
      content-class="log-image-viewer-modal"
      hide-header
      hide-footer
      @hidden="imageViewerIndex = 0"
    >
      <div class="d-flex flex-column h-100" style="min-height: 70vh;">
        <div class="d-flex align-items-center justify-content-between p-2 border-bottom bg-light">
          <span class="small text-muted">
            Entry {{ imageViewerIndex + 1 }} of {{ imageEntries.length }}
            <span v-if="currentViewerImageCount > 1">
              &mdash; Image {{ imageViewerImageIndex + 1 }} of {{ currentViewerImageCount }}
            </span>
          </span>
          <div>
            <b-button size="sm" variant="outline-primary" class="mr-1" :disabled="isFirstViewerImage()" @click="prevViewerImage">
              <i class="fas fa-chevron-left"></i> Previous
            </b-button>
            <b-button size="sm" variant="outline-primary" class="mr-1" :disabled="isLastViewerImage()" @click="nextViewerImage">
              Next <i class="fas fa-chevron-right"></i>
            </b-button>
            <b-button size="sm" variant="success" class="mr-1" :disabled="!currentViewerImageUrl" @click="downloadCurrentImage">
              <i class="fas fa-download"></i> Download
            </b-button>
            <b-button size="sm" variant="secondary" @click="showImageViewer = false">Close</b-button>
          </div>
        </div>
        <div class="flex-grow-1 d-flex align-items-center justify-content-center p-3 bg-dark overflow-auto">
          <div class="text-center">
            <img
              v-if="currentViewerImageUrl"
              :src="currentViewerImageUrl"
              class="log-viewer-img"
              alt="Log entry image"
            />
            <span v-else class="text-white">Loading image…</span>
            <div v-if="currentViewerImageCaption" class="mt-2 small text-light">
              {{ currentViewerImageCaption }}
            </div>
          </div>
        </div>
        <div v-if="currentViewerEntry" class="p-2 border-top bg-light small text-muted">
          {{ formatEntryDate(currentViewerEntry) }}{{ currentViewerEntry.notes ? ' — ' + currentViewerEntry.notes : '' }}
        </div>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import {
  getDatapointLogHistory,
  getBlockLogHistory,
  appendDatapointLogEntry,
  appendBlockLogEntry,
  updateDatapointLogEntry,
  updateBlockLogEntry,
  deleteDatapointLogEntry,
  deleteBlockLogEntry
} from '../services/HeaderLogService'
import Confirm from './Confirm'
import ImageUpload from './collector/ImageUpload'
import firebase from 'firebase/compat/app'
const fb = require('../firebaseConfig.js')

const PAGE_SIZE = 10

export default {
  name: 'LogHistoryModal',
  components: { Confirm, ImageUpload },
  props: {
    show: { type: Boolean, default: false },
    entityType: { type: String, required: true }, // 'datapoint' | 'block'
    entityId: { type: String, default: '' },
    collectorId: { type: String, default: '' },
    farmId: { type: String, default: '' },
    header: { type: Object, default: null },
    canEdit: { type: Boolean, default: true }
  },
  data () {
    return {
      isOpen: false,
      entries: [],
      lastDoc: null,
      loading: false,
      loadingMore: false,
      saving: false,
      newEntryDate: '',
      newEntryTimestampTime: '',
      newEntryValue: '',
      newEntryTime: '',
      newEntryNotes: '',
      newEntryImages: [],
      editingId: null,
      editValue: '',
      editTime: '',
      editNotes: '',
      editDate: '',
      editTimestampTime: '',
      editImages: [],
      confirmDeleteEntry: null,
      chartPoints: [],
      showImageViewer: false,
      imageViewerIndex: 0,
      imageViewerImageIndex: 0,
      imageUrls: {},
      thumbUrls: {}
    }
  },
  computed: {
    imageEntries () {
      if (this.logValueType !== 'image') return []
      return this.entries.filter(e => {
        const v = e.value
        if (!v) return false
        if (Array.isArray(v)) {
          return v.some(img => img && typeof img === 'object' && (img.path || img.url || img.small || img.large))
        }
        return typeof v === 'object' && (v.path || v.url || v.small || v.large)
      })
    },
    currentViewerEntry () {
      return this.imageEntries[this.imageViewerIndex] || null
    },
    currentViewerImageUrl () {
      if (!this.currentViewerEntry) return null
      const list = this.imageUrls[this.currentViewerEntry.id]
      if (!list || !list.length) return null
      return list[this.imageViewerImageIndex] || list[0]
    },
    currentViewerImageCount () {
      if (!this.currentViewerEntry) return 0
      const list = this.imageUrls[this.currentViewerEntry.id]
      return Array.isArray(list) ? list.length : 0
    },
    currentViewerImageCaption () {
      const entry = this.currentViewerEntry
      if (!entry || !entry.value) return ''
      const v = entry.value
      const idx = this.imageViewerImageIndex || 0
      if (Array.isArray(v)) {
        const img = v[idx] || v[0]
        return img && typeof img === 'object' ? (img.caption || '') : ''
      }
      if (typeof v === 'object') return v.caption || ''
      return ''
    },
    // Numeric and boolean headers can be plotted as a trend over time.
    isChartable () {
      return this.logValueType === 'number' || this.logValueType === 'boolean'
    },
    logValueType () {
      if (!this.header) return 'text'
      const t = (this.header.type || '').toLowerCase()
      if (t === 'number') return 'number'
      if (t === 'text') return 'text'
      if (t === 'select') return 'select'
      if (t === 'boolean') return 'boolean'
      if (t === 'date and time') return 'datetime'
      if (t === 'image') return 'image'
      if (this.header.logValueType === 'boolean') return 'boolean'
      if (this.header.logValueType === 'datetime') return 'datetime'
      if (this.header.logValueType === 'image') return 'image'
      return 'text'
    },
    hasRange () {
      // Match non-logged number behavior: only use slider when ALL of min, max, and step are set (truthy)
      return this.header && this.header.type === 'Number' && this.header.min && this.header.max && this.header.step
    },
    selectOptions () {
      const opts = [{ value: '', text: '—' }]
      ;(this.header?.options || []).forEach(opt => opts.push({ value: opt, text: opt }))
      return opts
    },
    booleanOptions () {
      return [
        { value: null, text: 'Select' },
        { value: true, text: 'True' },
        { value: false, text: 'False' }
      ]
    },
    newEntryValueValid () {
      if (this.logValueType === 'number') return this.newEntryValue !== '' && !isNaN(Number(this.newEntryValue))
      if (this.logValueType === 'boolean') return this.newEntryValue === true || this.newEntryValue === false
      if (this.logValueType === 'datetime') return String(this.newEntryValue).trim() !== ''
      if (this.logValueType === 'image') return Array.isArray(this.newEntryImages) && this.newEntryImages.length > 0
      return String(this.newEntryValue).trim() !== ''
    }
  },
  watch: {
    show (v) {
      this.isOpen = v
    }
  },
  methods: {
    setDefaultDate () {
      const d = new Date()
      this.newEntryDate = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
      this.newEntryTimestampTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0')
    },
    onNewEntryImageDelivered (payload) {
      this.newEntryImages = Array.isArray(payload) ? payload : []
    },
    onShow () {
      this.setDefaultDate()
      // Reset add-entry + edit state so drafts from another header don't leak in
      this.newEntryValue = ''
      this.newEntryTime = ''
      this.newEntryNotes = ''
      this.newEntryImages = []
      this.editValue = ''
      this.editTime = ''
      this.editNotes = ''
      this.editDate = ''
      this.editTimestampTime = ''
      this.editImages = []
      this.entries = []
      this.lastDoc = null
      this.imageUrls = {}
      this.loading = true
      this.editingId = null
      this.confirmDeleteEntry = null
      this.chartPoints = []
      this.fetchHistory()
      this.fetchChartData()
    },
    onHidden () {
      this.showImageViewer = false
      this.imageViewerIndex = 0
      this.imageViewerImageIndex = 0
      this.destroyChart()
      this.$emit('close')
    },
    onEditImagesDelivered (payload) {
      this.editImages = Array.isArray(payload) ? payload : []
    },
    close () {
      this.isOpen = false
    },
    async fetchHistory (append = false) {
      if (!this.header) return
      const limit = PAGE_SIZE
      const startAfter = append ? this.lastDoc : null
      if (this.entityType === 'datapoint') {
        const res = await getDatapointLogHistory({
          collectorId: this.collectorId,
          dataPointId: this.entityId,
          headerMachineName: this.header.machine_name,
          limit,
          startAfterDoc: startAfter
        })
        if (append) this.entries = this.entries.concat(res.entries)
        else this.entries = res.entries
        this.lastDoc = res.lastDoc
      } else {
        const res = await getBlockLogHistory({
          blockId: this.entityId,
          headerMachineName: this.header.machine_name,
          limit,
          startAfterDoc: startAfter
        })
        if (append) this.entries = this.entries.concat(res.entries)
        else this.entries = res.entries
        this.lastDoc = res.lastDoc
      }
      this.loading = false
      this.loadingMore = false
      this.resolveEntryImageUrls()
    },
    async resolveEntryImageUrls () {
      if (this.logValueType !== 'image' || !this.entries.length) return
      for (const entry of this.entries) {
        const v = entry.value
        if (!v) continue

        // Pick a representative image for this entry (first with a usable path/url)
        let img = null
        if (Array.isArray(v)) {
          img = v.find(it => it && typeof it === 'object' && (it.path || it.url || it.small || it.large)) || null
        } else if (typeof v === 'object') {
          img = v
        }
        if (!img || typeof img !== 'object') continue

        // If we've already resolved URLs for this entry, skip
        if (this.imageUrls[entry.id] && this.imageUrls[entry.id].length && this.thumbUrls[entry.id] && this.thumbUrls[entry.id].length) continue

        const images = Array.isArray(v) ? v : [img]
        const thumbList = []
        const fullList = []

        for (const image of images) {
          if (!image || typeof image !== 'object') continue
          const url = image.url
          const thumbPath = image.small || image.path
          const fullPath = image.large || image.path

          if (!thumbPath && !fullPath && !url) continue

          try {
            if (url) {
              thumbList.push(url)
              fullList.push(url)
              continue
            }

            if (thumbPath && fullPath && thumbPath !== fullPath) {
              const [thumbUrl, fullUrl] = await Promise.all([
                firebase.storage().ref(thumbPath).getDownloadURL(),
                firebase.storage().ref(fullPath).getDownloadURL()
              ])
              thumbList.push(thumbUrl)
              fullList.push(fullUrl)
            } else {
              const usedPath = thumbPath || fullPath
              if (!usedPath) continue
              const downloadUrl = await firebase.storage().ref(usedPath).getDownloadURL()
              thumbList.push(downloadUrl)
              fullList.push(downloadUrl)
            }
          } catch (e) {
            console.warn('Could not resolve image URLs for entry image', entry.id, e)
          }
        }

        if (thumbList.length) this.$set(this.thumbUrls, entry.id, thumbList)
        if (fullList.length) this.$set(this.imageUrls, entry.id, fullList)
      }
    },
    openImageViewer (entry) {
      const idx = this.imageEntries.findIndex(e => e.id === entry.id)
      this.imageViewerIndex = idx >= 0 ? idx : 0
      this.imageViewerImageIndex = 0
      this.showImageViewer = true
    },
    isFirstViewerImage () {
      if (!this.currentViewerEntry) return true
      return this.imageViewerIndex === 0 && this.imageViewerImageIndex === 0
    },
    isLastViewerImage () {
      if (!this.currentViewerEntry) return true
      const lastEntryIndex = this.imageEntries.length - 1
      const lastImageIndex = Math.max(0, this.currentViewerImageCount - 1)
      return this.imageViewerIndex === lastEntryIndex && this.imageViewerImageIndex === lastImageIndex
    },
    prevViewerImage () {
      if (!this.currentViewerEntry) return
      if (this.imageViewerImageIndex > 0) {
        this.imageViewerImageIndex--
        return
      }
      if (this.imageViewerIndex > 0) {
        this.imageViewerIndex--
        const lastImageIndex = Math.max(0, (this.currentViewerImageCount || 1) - 1)
        this.imageViewerImageIndex = lastImageIndex
      }
    },
    nextViewerImage () {
      if (!this.currentViewerEntry) return
      const lastImageIndex = Math.max(0, (this.currentViewerImageCount || 1) - 1)
      if (this.imageViewerImageIndex < lastImageIndex) {
        this.imageViewerImageIndex++
        return
      }
      if (this.imageViewerIndex < this.imageEntries.length - 1) {
        this.imageViewerIndex++
        this.imageViewerImageIndex = 0
      }
    },
    async downloadCurrentImage () {
      const url = this.currentViewerImageUrl
      if (!url) return
      const entry = this.currentViewerEntry
      const dateStr = entry && entry.timestamp && entry.timestamp.toDate
        ? entry.timestamp.toDate().toISOString().slice(0, 10)
        : 'image'
      const filename = `log-${dateStr}-${entry ? entry.id : 'image'}.jpg`
      try {
        const res = await fetch(url, { mode: 'cors' })
        const blob = await res.blob()
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = filename
        a.rel = 'noopener'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(a.href)
      } catch (e) {
        window.open(url, '_blank', 'noopener')
      }
    },
    async loadMore () {
      this.loadingMore = true
      await this.fetchHistory(true)
    },
    // Pull a deeper window of entries (independent of the paginated list)
    // and render a line chart of value-over-time for numeric/boolean headers.
    async fetchChartData () {
      if (!this.isChartable || !this.header) {
        this.chartPoints = []
        this.destroyChart()
        return
      }
      try {
        const res = this.entityType === 'datapoint'
          ? await getDatapointLogHistory({ collectorId: this.collectorId, dataPointId: this.entityId, headerMachineName: this.header.machine_name, limit: 500 })
          : await getBlockLogHistory({ blockId: this.entityId, headerMachineName: this.header.machine_name, limit: 500 })
        const pts = (res.entries || []).map(e => {
          const d = e.timestamp && e.timestamp.toDate ? e.timestamp.toDate() : null
          const y = this.logValueType === 'boolean'
            ? (e.value === true || e.value === 'true' ? 1 : 0)
            : Number(e.value)
          return d && Number.isFinite(y) ? { x: d, y } : null
        }).filter(Boolean)
        pts.sort((a, b) => a.x - b.x)
        this.chartPoints = pts
        this.$nextTick(() => this.renderChart())
      } catch (e) {
        console.warn('Could not load log trend data', e)
        this.chartPoints = []
        this.destroyChart()
      }
    },
    renderChart () {
      const Ctor = this.$Chart || (typeof window !== 'undefined' && window.Chart)
      const canvas = this.$refs.logChart
      if (!Ctor || !canvas || this.chartPoints.length < 2) {
        this.destroyChart()
        return
      }
      this.destroyChart()
      const isBool = this.logValueType === 'boolean'
      this._chart = new Ctor(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels: this.chartPoints.map(p => p.x.toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: 'numeric' })),
          datasets: [{
            label: this.header.label,
            data: this.chartPoints.map(p => p.y),
            borderColor: '#066739',
            backgroundColor: 'rgba(6,103,57,0.12)',
            fill: true,
            tension: 0.25,
            pointRadius: 3
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: isBool
              ? { min: 0, max: 1, ticks: { stepSize: 1, callback: v => (v ? 'True' : 'False') } }
              : { beginAtZero: false }
          }
        }
      })
    },
    destroyChart () {
      if (this._chart) {
        try { this._chart.destroy() } catch (e) { /* ignore */ }
        this._chart = null
      }
    },
    async addEntry () {
      if (!this.newEntryValueValid || this.saving) return
      this.saving = true
      const dateStr = this.newEntryDate
      const timeStr = (this.newEntryTimestampTime || '12:00:00').substring(0, 8)
      const date = dateStr ? new Date(dateStr + 'T' + timeStr) : new Date()
      let value = this.newEntryValue
      if (this.logValueType === 'number') value = Number(value)
      if (this.logValueType === 'boolean') value = value === true || value === 'true'
      if (this.logValueType === 'image') value = Array.isArray(this.newEntryImages) ? this.newEntryImages : []
      if (this.logValueType === 'datetime') {
        const time = (this.newEntryTime || '00:00:00').substring(0, 8)
        value = firebase.firestore.Timestamp.fromDate(new Date(this.newEntryValue + 'T' + time))
      }
      try {
        if (this.entityType === 'datapoint') {
          await appendDatapointLogEntry({
            collectorId: this.collectorId,
            dataPointId: this.entityId,
            farmId: this.farmId,
            header: this.header,
            value,
            notes: this.newEntryNotes.trim(),
            userId: fb.auth.currentUser?.uid,
            timestamp: date
          })
        } else {
          await appendBlockLogEntry({
            blockId: this.entityId,
            farmId: this.farmId,
            header: this.header,
            value,
            notes: this.newEntryNotes.trim(),
            userId: fb.auth.currentUser?.uid,
            timestamp: date
          })
        }
      this.newEntryValue = ''
      this.newEntryTime = ''
      this.newEntryNotes = ''
      this.newEntryImages = []
      this.setDefaultDate()
        await this.fetchHistory()
        this.fetchChartData()
        this.$emit('updated')
      } catch (e) {
        console.error(e)
        this.$store.commit('setAlert', { active: true, message: 'Failed to add log entry.', level: 'alert-danger', timeout: 4000 })
      }
      this.saving = false
    },
    startEdit (entry) {
      this.editingId = entry.id
      if (this.logValueType === 'boolean') {
        this.editValue = entry.value === true || entry.value === 'true'
      } else if (this.logValueType === 'datetime' && entry.value && typeof entry.value.toDate === 'function') {
        const d = entry.value.toDate()
        this.editValue = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
        this.editTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0')
      } else if (this.logValueType === 'datetime') {
        this.editValue = ''
        this.editTime = '00:00:00'
      } else if (this.logValueType === 'image') {
        this.editValue = '(image)'
        const v = entry.value
        if (Array.isArray(v)) {
          this.editImages = v.slice()
        } else if (v && typeof v === 'object') {
          this.editImages = [v]
        } else {
          this.editImages = []
        }
      } else {
        this.editValue = entry.value !== undefined && entry.value !== null ? String(entry.value) : ''
      }
      this.editNotes = entry.notes || ''
      if (entry.timestamp && entry.timestamp.toDate) {
        const d = entry.timestamp.toDate()
        this.editDate = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
        this.editTimestampTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0')
      } else {
        this.setDefaultDate()
        this.editDate = this.newEntryDate
        this.editTimestampTime = this.newEntryTimestampTime
      }
    },
    async saveEdit (entry) {
      this.saving = true
      let value = this.editValue
      if (this.logValueType === 'number') value = Number(value)
      if (this.logValueType === 'boolean') value = value === true || value === 'true'
      if (this.logValueType === 'datetime') {
        const time = (this.editTime || '00:00:00').substring(0, 8)
        value = firebase.firestore.Timestamp.fromDate(new Date(this.editValue + 'T' + time))
      }
      if (this.logValueType === 'image') value = Array.isArray(this.editImages) ? this.editImages : (entry.value || [])
      const dateStr = this.editDate
      const timeStr = (this.editTimestampTime || '12:00:00').substring(0, 8)
      const timestamp = dateStr ? new Date(dateStr + 'T' + timeStr) : undefined
      try {
        if (this.entityType === 'datapoint') {
          await updateDatapointLogEntry({
            collectorId: this.collectorId,
            dataPointId: this.entityId,
            header: this.header,
            entryId: entry.id,
            value,
            notes: this.editNotes.trim(),
            timestamp
          })
        } else {
          await updateBlockLogEntry({
            blockId: this.entityId,
            header: this.header,
            entryId: entry.id,
            value,
            notes: this.editNotes.trim(),
            timestamp
          })
        }
        this.editingId = null
        await this.fetchHistory()
        this.fetchChartData()
        this.$emit('updated')
      } catch (e) {
        console.error(e)
        this.$store.commit('setAlert', { active: true, message: 'Failed to update log entry.', level: 'alert-danger', timeout: 4000 })
      }
      this.saving = false
    },
    confirmDelete (entry) {
      this.confirmDeleteEntry = entry
    },
    async doDelete (confirmed) {
      if (!confirmed) {
        this.confirmDeleteEntry = null
        return
      }
      const entry = this.confirmDeleteEntry
      this.confirmDeleteEntry = null
      this.saving = true
      try {
        if (this.entityType === 'datapoint') {
          await deleteDatapointLogEntry({
            collectorId: this.collectorId,
            dataPointId: this.entityId,
            header: this.header,
            entryId: entry.id
          })
        } else {
          await deleteBlockLogEntry({
            blockId: this.entityId,
            header: this.header,
            entryId: entry.id
          })
        }
        this.entries = this.entries.filter(e => e.id !== entry.id)
        this.fetchChartData()
        this.$emit('updated')
      } catch (e) {
        console.error(e)
        this.$store.commit('setAlert', { active: true, message: 'Failed to delete log entry.', level: 'alert-danger', timeout: 4000 })
      }
      this.saving = false
    },
    formatEntryDate (entry) {
      if (!entry.timestamp || !entry.timestamp.toDate) return '—'
      const d = entry.timestamp.toDate()
      const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0
      return hasTime
        ? d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
        : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatEntryValue (entry) {
      const v = entry.value
      if (v === undefined || v === null) return '—'
      if (this.logValueType === 'datetime' && v && typeof v.toDate === 'function') {
        const d = v.toDate()
        return this.header && this.header.includeTime
          ? d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
          : d.toLocaleDateString(undefined, { dateStyle: 'short' })
      }
      if (this.logValueType === 'image' && v) {
        if (Array.isArray(v)) {
          const count = v.filter(img => img && typeof img === 'object' && (img.path || img.url || img.small || img.large)).length
          if (!count) return '—'
          return count === 1 ? '1 image' : `${count} images`
        }
        if (typeof v === 'object') return '1 image'
      }
      const suffix = this.header && this.header.suffix ? ' ' + this.header.suffix : ''
      return String(v) + suffix
    },
    getImageCount (entry) {
      const v = entry && entry.value
      if (!v) return 0
      if (Array.isArray(v)) {
        return v.filter(img => img && typeof img === 'object' && (img.path || img.url || img.small || img.large)).length
      }
      if (typeof v === 'object' && (v.path || v.url || v.small || v.large)) return 1
      return 0
    }
  }
}
</script>

<style scoped>
/* Add entry: relative on top, no overflow/fixed height so datepicker popup is never clipped */
.log-history-add-section {
  position: relative;
  overflow: visible;
  flex-shrink: 0;
}
/* History: fixed height with scroll; only this section scrolls */
.log-history-history-section {
  overflow-y: auto;
  overflow-x: hidden;
}
.log-history-chart-section {
  flex-shrink: 0;
}
.log-history-chart-wrap {
  position: relative;
  height: 200px;
}
.log-history-thumb {
  max-width: 80px;
  max-height: 48px;
  object-fit: cover;
  cursor: pointer;
}
.log-viewer-img {
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
}
</style>

<!-- Unscoped: modal is portaled to body so these must be global -->
<style>
.log-history-modal .modal-body {
  overflow: visible !important;
  max-height: none !important;
  display: flex !important;
  flex-direction: column !important;
}
.log-history-modal .modal-content {
  overflow: visible !important;
  max-height: none !important;
}
.log-history-modal .modal-dialog {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.log-history-modal .modal-body .log-history-modal-content {
  display: flex;
  flex-direction: column;
  overflow: visible;
  min-height: 0;
}
.log-history-modal .dropdown-menu {
  z-index: 1100 !important;
}
/* When editing a row, don't clip the datepicker calendar (it opens inside the scroll area) */
.log-history-modal .log-history-history-section.log-history-editing-active {
  overflow: visible !important;
}
.log-image-viewer-modal .modal-dialog {
  max-width: 95vw;
  max-height: 90vh;
}
.log-image-viewer-modal .modal-body {
  max-height: 90vh;
  overflow: hidden;
}
</style>
